/**
 * Marquee sanitario — recolocacion bajo las fotos
 *
 * El sello va debajo de la galeria, pero el bloque `_product-media-gallery` de
 * Horizon no acepta bloques hijos: desde el editor este bloque solo se puede
 * colgar de la columna de compra. La alternativa era duplicarlo como seccion
 * aparte y reproducir a mano el ancho y el margen de la columna de fotos, que
 * salen de la rejilla `--full-page-grid-*`. Se mueve el nodo y hereda las dos
 * medidas exactas, sin copiar nada.
 *
 * Va como custom element y no como script suelto por dos razones:
 * `connectedCallback` corre mientras la pagina se parsea, antes del primer
 * pintado, asi que no hay salto visible; y vuelve a correr solo cuando el
 * editor re-renderiza el bloque, sin observadores ni eventos de seccion.
 */
class RitTrustMarquee extends HTMLElement {
  connectedCallback() {
    if (this.dataset.destino !== 'media' || this.dataset.colocado) return;
    if (!this.colocar()) {
      // La columna de fotos se rinde antes que la de compra, asi que el destino
      // suele existir ya. Si el editor invierte el orden, se reintenta al final.
      document.addEventListener('DOMContentLoaded', () => this.colocar(), { once: true });
    }
  }

  colocar() {
    const destino = document.querySelector('.product-information__media .sticky-content');
    if (!destino || destino.contains(this)) return false;
    // Se marca antes de mover: appendChild desconecta y reconecta el nodo, y sin
    // esto connectedCallback volveria a entrar.
    this.dataset.colocado = 'si';
    destino.appendChild(this);
    this.rellenar();
    return true;
  }

  /**
   * El bucle se hace con dos mitades identicas y un `translate` de -50%: la
   * segunda cae exactamente donde estaba la primera y el salto no se ve. Eso
   * solo funciona si cada mitad es al menos tan ancha como la banda; si no, se
   * ve el final de la cinta con el fondo desnudo detras antes de que vuelva a
   * empezar.
   *
   * Con dos sellos la mitad medía 324px contra 509px de banda. Se clona el
   * grupo base las veces que haga falta hasta cubrir la banda, y se rehacen las
   * dos mitades con ese numero. El -50% sigue valiendo porque las dos mitades
   * siguen siendo iguales.
   */
  rellenar() {
    const pista = this.querySelector('.trust-marquee__track');
    const mirilla = this.querySelector('.trust-marquee__viewport');
    if (!pista || !mirilla) return;

    const base = this.base || (this.base = pista.firstElementChild.cloneNode(true));
    const anchoBase = pista.firstElementChild.getBoundingClientRect().width;
    const anchoBanda = mirilla.getBoundingClientRect().width;
    if (!anchoBase || !anchoBanda) return;

    const copias = Math.max(1, Math.ceil(anchoBanda / anchoBase));
    if (this.copias === copias) return;
    this.copias = copias;

    pista.replaceChildren();
    for (let mitad = 0; mitad < 2; mitad += 1) {
      for (let i = 0; i < copias; i += 1) {
        const grupo = base.cloneNode(true);
        // Solo la primera copia de la primera mitad se anuncia: el resto es
        // repeticion visual y un lector de pantalla no tiene por que oirla.
        if (mitad > 0 || i > 0) grupo.setAttribute('aria-hidden', 'true');
        pista.append(grupo);
      }
    }
  }
}

// El ancho de la banda cambia con la ventana y con el tope de la galeria, asi
// que el numero de copias se revisa al redimensionar.
addEventListener('resize', () => {
  document.querySelectorAll('rit-trust-marquee').forEach((m) => m.rellenar?.());
});

if (!customElements.get('rit-trust-marquee')) {
  customElements.define('rit-trust-marquee', RitTrustMarquee);
}
